import styled from "@emotion/styled";
import { FC, useId } from "react";

import { ChevronDown, ChevronRight } from "@livechat/design-system-icons";
import { DesignToken, Heading, Icon, RadiusToken, SpacingToken } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { notDraggableStyles } from "styles/common";
import { withVar } from "themes/helpers";

interface IProps {
  title: string | React.ReactElement;
  icon?: React.ReactElement;
  opened?: boolean;
  onHeaderClick: () => void;
  children: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  headerTestID?: string;
}

export const AccordionItem: FC<IProps> = ({
  title,
  icon,
  opened,
  onHeaderClick,
  children,
  fullWidth,
  className,
  headerTestID,
}) => {
  const containerId = useId();
  return (
    <Container opened={opened} fullWidth={fullWidth} className={className} id={containerId}>
      <Header
        onClick={onHeaderClick}
        data-testid={headerTestID ?? `accordion-${title.toString()}`}
        aria-expanded={opened}
        aria-controls={containerId}
      >
        {icon && <ProviderIcon>{icon}</ProviderIcon>}
        <Heading size="xs">{title}</Heading>
        <ArrowIcon source={opened ? ChevronDown : ChevronRight} />
      </Header>
      <ContentContainer opened={opened}>
        <Content>{opened && children}</Content>
      </ContentContainer>
    </Container>
  );
};

const Container = styled.div<{ opened?: boolean; fullWidth?: boolean }>`
  border-radius: 4px;
  max-width: 560px;
  width: 100%;
  margin-bottom: ${withVar(SpacingToken.Spacing3)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
  border: 1px solid
    ${(props): string => withVar(props.opened ? DesignToken.BorderBasicPrimary : DesignToken.BorderBasicSecondary)};
  ${(props): string => (props.fullWidth ? "max-width: 100%;" : "")};
  text-align: left;
  box-sizing: border-box;
`;

const Header = styled(Flex)`
  justify-content: flex-start;
  align-items: center;
  padding: ${withVar(SpacingToken.Spacing4)} ${withVar(SpacingToken.Spacing5)};
  cursor: pointer;
  ${notDraggableStyles}
`;

const ArrowIcon = styled(Icon)`
  margin-left: auto;
`;

const ProviderIcon = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 12px;
`;

const ContentContainer = styled.div<{ opened?: boolean }>`
  overflow: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => (props.opened ? "" : "hidden")
  };
  opacity: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => (props.opened ? 1 : 0)
  };
  max-height: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => (props.opened ? "100%" : "0")
  };
`;

const Content = styled.div`
  padding: 0px ${withVar(SpacingToken.Spacing5)} ${withVar(SpacingToken.Spacing4)};
`;
